/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import {
  AccountType,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Checkbox,
  FAIcon,
  IconStyle,
  Input,
  InputTypes,
  NextgenImage,
  RadioButton,
  Drawer,
  Theme,
  useBreakpoint,
  useMasterData,
  useTranslation,
  useSession,
  Breakpoints,
  LoaderType,
} from '@nextgen-web-framework/all';
import {
  Citizenship,
  DynamicObjectInterface,
  PersonalIdRequestTypeEnum,
  RegistrationPatchRequestConsentAcceptedEnum,
  updateRegistrationData,
  ImageReturnType,
  type FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import RegistrationCIDVerification from '../RegistrationCIDVerification/RegistrationCIDVerification';
import RegistrationSignIn from '../RegistrationSignIn/RegistrationSignIn';
import HtmlContent from '../Common/HtmlContent/HtmlContent';
import { RegistrationCIDVerificationProps } from '../RegistrationCIDVerification/types/propTypes';
import { useRegistrationFieldsMasterData } from '../../utils/masterdata';
import {
  getRegistrationOptions,
  getRegistrationValidationData,
  getRegistrationValidationDataByPk,
  isValidSpouseVerification,
  registrationValidationSchema,
} from '../../utils/registrationUtils';
import { iff, getMarketLocale, hasContent, parseObject } from '../../utils/helper';
import { fetchTermsNConditionsCMSIfUndefined } from '../../utils/ProfileCMSUtils';
import { COAPP_STEPS, ErrorStatusConstants, MASTER_DATA_PK } from '../../utils/Constants';
import SearchDrawer from '../Common/SearchDrawer/SearchDrawer';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import {
  PatchHandlerType,
  RegistrationIdVerificationStepProps,
  RegistrationValidations,
  CitizenshipDrodownType,
} from './types/propTypes';
import styles from './RegistrationIdVerificationStep.module.scss';

const RegistrationIdVerificationStep = ({
  nextHandler,
  verifySpouseOnly = false,
  desktopImage,
  className,
  mobileImageClassName,
  recordType,
}: RegistrationIdVerificationStepProps) => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const viewPortType = useBreakpoint();
  const isDesktopView =
    [Breakpoints.XS, Breakpoints.SM, Breakpoints.MD].indexOf(viewPortType as Breakpoints) !== -1 ? false : true;

  const errorRef = useRef<FunctionType | null>(null);

  const { session } = useSession();
  const { aboNum: accountNum = '' } = session;
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { cidVerification, userData, currentStepName, currentIndex, sourceUrl } = registrationFlowData;
  const { isUserCidVerified, isRadioChanged, isSpouse, isSpouseVerified } = cidVerification;
  const { patchVersion } = userData;
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  //tnc
  const termsNConditionsCmsData = useSelector((state: AppState) => state.commerceProfile.termsNConditionsCMS);
  const termsAndConditions = termsNConditionsCmsData?.[selectedAccountType]?.termsAndConditions;
  // cms image
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const { coApp } = registrationCmsData;
  const { tnc: coAppTermsAndConditions } = parseObject(coApp);
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const idVerificationImage = accountTypeImagesCmsData?.[selectedAccountType]?.idVerificationImage;
  const idVerificationStepImage =
    idVerificationImage?.length > 1 &&
    idVerificationImage?.find((item: ImageReturnType) => item?.viewport.includes(viewPortType));
  const apiErrorHandler = useGlobalErrorHandler();

  // masterdata
  const config = useMasterData<RegistrationValidations[]>('RegistrationFieldsMasterData');
  const citizenshipField = config?.find((c: RegistrationValidations) => c.pk === 'citizenshipCountry');
  const citizenDropdownValues = citizenshipField?.optionsWithLabels?.ALL;
  //Display Message to be passed in props to child component
  const [userMessage, setUserMessage] = useState('');
  const [spouseMessage, setSpouseMessage] = useState('');
  //citizenship state
  const [citizenship, setCitizenship] = useState('');

  //Radio states
  const [thaiCitizen, setThaiCitizen] = useState(true);
  //disbale verify
  const [disableVerifyUser, setDisableVerifyUser] = useState(true);
  const [disableVerifySpouse, setDisableVerifySpouse] = useState(true);
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();
  const personalIdPk = [MASTER_DATA_PK.USER_PASSPORT, MASTER_DATA_PK.USER_CID];
  const spouseIdPk = [MASTER_DATA_PK.SPOUSE_CID, MASTER_DATA_PK.SPOUSE_PASSPORT];

  // This key is for co-app phase 2, which is allow CID & Passport
  // When using this, the label will change to ID Number instead of CID/Passport
  const isCombineValidation = currentStepName === COAPP_STEPS.P2_ID_STEP;

  const getValidationSchema = (pk: string[]) => {
    const validationField = getRegistrationValidationDataByPk({
      config: registrationFieldsMasterData as RegistrationValidations[],
      pk: pk,
    });

    const validationSchema = registrationValidationSchema(validationField, selectedAccountType, false);
    return validationSchema;
  };

  const clearState = () => {
    const updateMember = !isABO
      ? { isUserCidVerified: false, isUser: false }
      : { isSpouseVerified: false, isSpouse: false, spousePersonalId: '', spouseRecordType: '' };
    setCitizenship('');
    setSpouseMessage('');
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        cidVerification: {
          ...cidVerification,
          ...updateMember,
          isRadioChanged: !isRadioChanged,
        },
      }),
    );
  };
  const handlerThaiCitizenRadio = () => {
    if (thaiCitizen) {
      return;
    }
    setThaiCitizen((prev) => !prev);
    clearState();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // foreigner tnc is in english
    const tncLocale = iff(!thaiCitizen, 'en-us', getMarketLocale());
    fetchTermsNConditionsCMSIfUndefined(
      dispatch,
      termsNConditionsCmsData,
      selectedAccountType,
      tncLocale,
      apiErrorHandler,
    );
  }, [thaiCitizen]);

  const filteredCountryData = citizenDropdownValues?.map((country: CitizenshipDrodownType) => ({
    ...country,
    key: country.value,
    value: t(`profile.RegistrationIdVerificationStep.country.${country.value}`),
  }));

  const handlerForeignerRadio = () => {
    if (!thaiCitizen) {
      return;
    }
    setThaiCitizen((prev) => !prev);
    clearState();
  };
  const isABO = selectedAccountType === AccountType.BusinessOwner;
  const idTypeABO = PersonalIdRequestTypeEnum.Cid;
  const idTypeMember = iff(thaiCitizen, PersonalIdRequestTypeEnum.Cid, PersonalIdRequestTypeEnum.Passport);
  const idTypeSpouse = iff(verifySpouseOnly && isCombineValidation, recordType, idTypeMember); // Use recordType for co-app phase2 only
  const idType = iff(isABO, idTypeABO, idTypeMember); // No impact to primary for idTypeSpouse

  const patchHandler = async ({ payload, isNextButton, slicePayload }: PatchHandlerType) => {
    const finalPayload = {
      ...payload,
      version: patchVersion,
    };

    // eslint-disable-next-line
    const res: any = await Promise.resolve(nextHandler(finalPayload, slicePayload, !isNextButton));

    return res;
  };
  const spouseCitzienship = thaiCitizen ? Citizenship.Citizen : Citizenship.Foreigner;

  const verifyUser = async (name: string, value: string, setErrors: FunctionType, setShowLoading: FunctionType) => {
    setShowLoading(true);
    setUserMessage('');
    const newRegData = {
      ...registrationFlowData,
      cidVerification: {
        ...cidVerification,
        isUser: true,
        isUserCidVerified: true,
        verifyUser: true,
      },
    };

    const payload = {
      personalId: { recordType: idType, recordNumber: value },
      citizenship: isABO ? Citizenship.Citizen : spouseCitzienship,
      citizenshipCountry: isABO ? '' : citizenship,
    };
    const res = await patchHandler({ payload, isNextButton: false, slicePayload: newRegData });
    setShowLoading(false);
    if (res?.responseBody) {
      const { code, status } = res.responseBody;
      if (status === ErrorStatusConstants.STATUS_503 || ErrorStatusConstants.STATUS_504) {
        setDisableVerifyUser(false);
      }
      const updateError = code?.length ? `${code}` : '';
      const errorKey = updateError ? `profile.RegistrationCIDVerification.${updateError}` : '';
      setErrors(name, errorKey);
    } else {
      setUserMessage('profile.RegistrationIdVerificationStep.SucessMessage');
      setDisableVerifyUser(true);
    }
  };
  const verifySpouse = async (name: string, value: string, setErrors: FunctionType, setShowLoading: FunctionType) => {
    // Save setError into ref
    errorRef.current = (code: string) => setErrors(name, code);

    setShowLoading(true);
    setSpouseMessage('');
    const newRegData = {
      ...registrationFlowData,
      cidVerification: {
        ...cidVerification,
        isSpouse: true,
        isSpouseVerified: true,
        // For Co-App
        spousePersonalId: value,
        spouseRecordType: idTypeSpouse,
      },
    };
    dispatch(updateRegistrationData(newRegData));
    const payload = {
      spousePersonalId: { recordType: idTypeSpouse, recordNumber: value },
      spouseCitizenship: spouseCitzienship,
      spouseCitizenshipCountry: citizenship,
    };
    const res = await patchHandler({ payload, isNextButton: false, slicePayload: newRegData });
    setShowLoading(false);
    if (res.responseBody) {
      const { code, status } = res.responseBody;
      if (status === ErrorStatusConstants.STATUS_503 || ErrorStatusConstants.STATUS_504) {
        setDisableVerifySpouse(false);
      }
      const updateError = code?.length ? `${code}` : '';

      let errorCode = updateError;
      // for co-app phase 2 only
      if (
        verifySpouseOnly &&
        updateError === 'comregistrationas-id-mismatch' &&
        currentStepName === COAPP_STEPS.P2_ID_STEP
      ) {
        errorCode = `coapp.${idTypeSpouse}.${updateError}`;
      }

      const errorKey = updateError ? `profile.RegistrationCIDVerification.${errorCode}` : '';
      setErrors(name, errorKey);
    } else {
      if (isValidSpouseVerification(verifySpouseOnly, res)) {
        setSpouseMessage('profile.RegistrationIdVerificationStep.SucessMessage');
        setDisableVerifySpouse(true);
      }
    }
  };
  const getLabel = (id: string) =>
    iff(
      id === PersonalIdRequestTypeEnum.Cid,
      'profile.RegistrationCIDVerification.CID',
      'profile.RegistrationCIDVerification.passport',
    );

  const isCitizenship = iff(thaiCitizen, false, citizenship.length === 0);
  const CIDArgs = {
    idType,
    label: getLabel(idType),
    callback: verifyUser,
    message: isUserCidVerified && userMessage,
    name: idType.toLocaleLowerCase(),
    isABO,
    isUser: true,
    disabled: iff(isABO, false, isCitizenship),
    disableVerify: disableVerifyUser,
    updateDisableVerify: setDisableVerifyUser,
    validationSchema: getValidationSchema(personalIdPk),
  };
  //spouse ID verification state
  const [showSpouseIdInput, setShowSpouseIdInput] = useState(false);
  const spouseCIDArgs = {
    idType: idTypeSpouse,
    callback: verifySpouse,
    message: isSpouse && isSpouseVerified && spouseMessage,
    disabled: isCitizenship,
    name: idTypeSpouse.toLocaleLowerCase(),
    isABO,
    label: getLabel(idTypeSpouse),
    isUser: false,
    disableVerify: disableVerifySpouse,
    updateDisableVerify: setDisableVerifySpouse,
    validationSchema: getValidationSchema(spouseIdPk),
    // For Co-app
    isPartner: verifySpouseOnly, // --> to show partner ID, if available
  };
  //checkbox states
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isTNCOpen, setIsTNCOpen] = useState(false);

  const showTnc = (e: DynamicObjectInterface) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTNCOpen(true);
  };
  const getTnc = (tncText?: string, tncLinkText?: string) => (
    <div className={styles['tnc-text']}>
      <span className={`${styles['tnc-textlink']} ${checkboxValue ? styles['tnc-textlink-checked'] : ''}`}>
        {tncText}
        <a
          className={styles['tnc-hyperlink']}
          role="presentation"
          onKeyDown={showTnc}
          onClick={showTnc}
          data-testid="tnc-checkbox-link"
        >
          {tncLinkText}
        </a>
      </span>
    </div>
  );

  const getLabels = () => {
    switch (currentStepName) {
      case 'coApplicantIdStep':
        return {
          header: t('profile.coApp.verificationIdTitle'),
          image: coApp?.registrationImage,
          citizenship: t('profile.coApp.nationalStatusLabel'),
          tnc: (
            <div className={styles['tnc-text']}>
              <span className={`${styles['tnc-textlink']} ${checkboxValue ? styles['tnc-textlink-checked'] : ''}`}>
                {t('profile.coApp.verificationTnc')}
              </span>
            </div>
          ),
          tncContent: '',
        };

      case 'coApplicantIdVerificationStep': {
        const tncText1 = t('profile.coApp.invitedTnc1');
        const tncText2 = t(`profile.coApp.invitedTnc2.${selectedAccountType?.toUpperCase()}`);
        const tncContentCheck = hasContent(coAppTermsAndConditions);
        const tncText = tncContentCheck ? getTnc(tncText1, tncText2) : `${tncText1} ${tncText2}`;

        return {
          header: t(`profile.coApp.invitedVerificationTitle.${selectedAccountType?.toUpperCase()}`),
          image: coApp?.registrationImage,
          citizenship: '',
          confirmIdentity: t(`profile.coApp.confirmIdentity.${idTypeSpouse}`),
          tnc: tncText,
          tncContent: coAppTermsAndConditions,
        };
      }
      default:
        return {
          header: t(`profile.RegistrationIdVerificationStep.${selectedAccountType?.toLowerCase()}`),
          image: idVerificationStepImage,
          citizenship: t('profile.RegistrationIdVerificationStep.nationalStatusLabel'),
          tnc: getTnc(
            t('profile.RegistrationIdVerificationStep.checkboxLabel'),
            t('profile.RegistrationIdVerificationStep.TNCcheckbox'),
          ),
          tncContent: termsAndConditions,
        };
    }
  };

  const labels = getLabels();

  const checkboxDrawer = () => {
    const buttonText = t('profile.RegistrationIdVerificationStep.ButtonText');

    const onClose = () => {
      setIsTNCOpen(false);
      setCheckboxValue(true);
    };
    return (
      <div data-testid="registration-change-username-drawer">
        <Drawer
          header={t('profile.RegistrationIdVerificationStep.TNCModalHeading')}
          closeIcon
          className="form-drawer"
          data-testid="drawer-testid"
          bottomGap={0}
          onClose={onClose}
          closeOnBackdropClick
          useReactPortal
          portalContainerId={'react-portal-drawer'}
          footer={
            <Button
              title={buttonText}
              buttonStyle={ButtonStyles.SOLID}
              onClick={onClose}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              data-testid="next-button"
            />
          }
        >
          <div className={styles['tnc-content']}>
            <HtmlContent content={labels?.tncContent} />
          </div>
        </Drawer>
      </div>
    );
  };

  const userMessageCheck = userMessage.length > 1 || verifySpouseOnly;
  const isUserVerified =
    (!verifySpouseOnly && isUserCidVerified) || // for registration
    (verifySpouseOnly && isSpouseVerified); // for co-app
  const disableButton = isUserVerified && checkboxValue && userMessageCheck && disableVerifySpouse && disableVerifyUser;

  //next handler
  const nextButtonHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      consentAccepted: [RegistrationPatchRequestConsentAcceptedEnum.Terms],
    };
    const isNextButton = true;
    patchHandler({ payload, isNextButton });
  };
  //citizenship from masterdata
  const inputs = ['citizenship'];
  const field = getRegistrationValidationData({
    config: registrationFieldsMasterData as RegistrationValidations[],
    fieldNames: inputs,
  });
  const citizenshipType = getRegistrationOptions(field, selectedAccountType);
  const showCIDInput = citizenshipType?.length === 1;

  // national status radio
  const selectNationalStatus = () => (
    <div className={styles['national-status-container']} data-testid="national-status-container">
      <div className={styles['national-status']} data-testid="national-status">
        {labels.citizenship}
      </div>
      <div className={styles['radio-fields']}>
        <RadioButton
          id="thai-citizen"
          data-testid="thai-citizen"
          className={styles['radio-button']}
          label={t('profile.RegistrationIdVerificationStep.thaiCitizenLabel')}
          checked={thaiCitizen}
          theme={thaiCitizen ? Theme.PURPLE : Theme.BLACK}
          onClick={handlerThaiCitizenRadio}
        />
        <RadioButton
          id="foreigner"
          data-testid="foreigner"
          className={styles['radio-button']}
          label={t('profile.RegistrationIdVerificationStep.foreignerLabel')}
          checked={!thaiCitizen}
          theme={!thaiCitizen ? Theme.PURPLE : Theme.BLACK}
          onClick={handlerForeignerRadio}
        />
      </div>
    </div>
  );

  const openCitizenDrawer = () => {
    setIsDrawerOpen(true);
  };
  const handleSearchDrawerClose = () => {
    setIsDrawerOpen(false);
  };
  const handleSearchDrawerSelect = (_: string, selectedCountry: CitizenshipDrodownType) => {
    setCitizenship(selectedCountry.key as string);
    setIsDrawerOpen(false);
    // Reset the disable verify state when a new country is selected
    if (errorRef.current && verifySpouseOnly) {
      setDisableVerifySpouse(false);
      errorRef.current(''); // reset error state
    }
  };

  const showInputComponent = (cidProps: RegistrationCIDVerificationProps) => (
    <div
      className={`${styles['show-national-status']} ${
        selectedAccountType === AccountType.Member ? styles['show-national-status-member'] : ''
      } ${verifySpouseOnly ? styles['co-app-ui'] : ''}`}
      data-testid="show-national-status"
    >
      {(labels.citizenship || !verifySpouseOnly) && selectNationalStatus()}
      {labels?.confirmIdentity && <div className={styles['confirmIdentity']}>{labels.confirmIdentity}</div>}
      {!thaiCitizen && (
        <div
          className={styles['citizenship']}
          data-testid="citizenship-input"
          onClick={openCitizenDrawer}
          tabIndex={-1}
          aria-hidden="true"
        >
          <Input
            id="citizenship"
            data-testid="citizenship"
            type={InputTypes.TEXT}
            name="citizenship"
            value={citizenship ? t(`profile.RegistrationIdVerificationStep.country.${citizenship}`) : ''}
            label={t('profile.RegistrationIdVerificationStep.citizenLabel')}
            maxLength={50}
            readOnly
            inputIcon={{ iconName: 'chevron-down', iconStyle: IconStyle.SOLID }}
          />
        </div>
      )}
      <RegistrationCIDVerification {...cidProps} />
    </div>
  );
  return (
    <DesktopLayout desktopImage={desktopImage ?? idVerificationStepImage} containerClassName={className}>
      <div className={styles['id-verification-wrapper']} data-testid="id-verification-wrapper">
        <div className={styles['id-verification-component']} data-testid="id-verification-component">
          <div className={styles['id-verification-container']} data-testid="id-verification-container">
            {isTNCOpen && checkboxDrawer()}
            <div className={styles['id-verification-header']} data-testid="id-verification-header">
              {labels.header}
            </div>
            {!isDesktopView && (
              <div
                className={`${styles['id-verification-image']} ${mobileImageClassName}`}
                data-testid="id-verification-image"
              >
                <NextgenImage
                  src={labels?.image?.url ?? idVerificationStepImage.url}
                  loaderType={LoaderType.CONTENTSTACK}
                  alt={labels?.image?.altText ?? idVerificationStepImage.altText}
                  layout="fixed"
                />
              </div>
            )}
            {!verifySpouseOnly && (
              <div className={styles['id-verification-cid']} data-testid="id-verification-cid">
                {showCIDInput ? <RegistrationCIDVerification {...CIDArgs} /> : showInputComponent(CIDArgs)}
              </div>
            )}
            {verifySpouseOnly && (
              <div className={styles['id-verification-cid']} data-testid="id-verification-cid">
                {showInputComponent(spouseCIDArgs)}
              </div>
            )}
            {!verifySpouseOnly && showCIDInput && (
              <div className={styles['id-verification-spouse-cid']} data-testid="id-verification-spouse-cid">
                <div
                  className={styles['verify-spouse']}
                  data-testid="verify-spouse"
                  onClick={() => {
                    if (userMessage.length === 0) {
                      return;
                    }
                    setShowSpouseIdInput((prev) => !prev);
                  }}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <div
                    className={`${styles['verify-spouse-text']} ${
                      userMessage.length > 1 ? styles['verify-spouse-text-clicked'] : ''
                    }`}
                  >
                    {t('profile.RegistrationIdVerificationStep.spouseVerifyLabel')}
                  </div>
                  <div className={styles['verify-spouse-icon']}>
                    <FAIcon
                      theme={userMessage.length > 1 ? Theme.BLACK : Theme.GRAY}
                      iconName={showSpouseIdInput ? 'chevron-up' : 'chevron-down'}
                      iconStyle={IconStyle.SOLID}
                      data-testid="icon-tesid"
                    />
                  </div>
                </div>
                {showSpouseIdInput && (
                  <div className={styles['verify-spouse-input']} data-testid="verify-spouse-input">
                    {showInputComponent(spouseCIDArgs)}
                  </div>
                )}
              </div>
            )}

            <div className={styles['id-verification-tnc-checkbox']}>
              <Checkbox
                data-testid="tnc-checkbox"
                text={labels.tnc}
                checked={checkboxValue}
                onChange={(e: React.ChangeEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCheckboxValue((prev) => !prev);
                }}
              />
            </div>
          </div>
          <div className={styles['id-verification-next-btn']}>
            <Button
              title={t('profile.common.nextButton')}
              data-testid="next-btn-testid"
              buttonStyle={ButtonStyles.SOLID}
              size={ButtonSize.MEDIUM}
              disabled={!disableButton}
              onClick={nextButtonHandler}
              buttonWidth={ButtonWidth.FULL_WIDTH}
            />
            {currentIndex === 0 && !accountNum && <RegistrationSignIn sourceUrl={sourceUrl} />}
          </div>
        </div>
        {isDrawerOpen && (
          <SearchDrawer
            fieldName="citizenship"
            title={t('profile.RegistrationIdVerificationStep.citizenLabel')}
            config={{ label: 'value', key: 'key' }}
            data={filteredCountryData as DynamicObjectInterface[]}
            callback={handleSearchDrawerSelect}
            onClose={handleSearchDrawerClose}
          />
        )}
      </div>
    </DesktopLayout>
  );
};
export default RegistrationIdVerificationStep;
